import React, { createContext, useContext, useState, useEffect } from 'react';

// 5 Professional Themes with full color definitions
const themes = {
  classic: {
    id: 'classic',
    name: 'Classic Professional',
    description: 'Clean and traditional design',
    colors: {
      primary: 'amber',
      secondary: 'slate',
      accent: '#d4af37',
      accentHover: '#c4a030',
      background: '#ffffff',
      backgroundDark: '#1a2332',
      backgroundMuted: '#f8fafc',
      text: '#1a2332',
      textLight: '#ffffff',
      textMuted: '#64748b',
      border: '#e2e8f0'
    },
    gradients: {
      hero: 'linear-gradient(135deg, #1a2332 0%, #2d3748 50%, #1e3a5f 100%)',
      heroClass: 'from-slate-900 via-slate-800 to-slate-900',
      card: 'from-white to-slate-50',
      button: 'from-amber-500 to-amber-600'
    },
    tailwind: {
      accent: 'amber-500',
      accentDark: 'amber-600',
      accentLight: 'amber-400',
      bg: 'slate-900',
      bgLight: 'slate-800'
    }
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean Blue',
    description: 'Calm and trustworthy blue tones',
    colors: {
      primary: 'blue',
      secondary: 'cyan',
      accent: '#3b82f6',
      accentHover: '#2563eb',
      background: '#ffffff',
      backgroundDark: '#0c1929',
      backgroundMuted: '#f0f9ff',
      text: '#0c1929',
      textLight: '#ffffff',
      textMuted: '#64748b',
      border: '#bfdbfe'
    },
    gradients: {
      hero: 'linear-gradient(135deg, #0c1929 0%, #1e3a5f 50%, #0c4a6e 100%)',
      heroClass: 'from-blue-950 via-blue-900 to-cyan-900',
      card: 'from-white to-blue-50',
      button: 'from-blue-500 to-cyan-500'
    },
    tailwind: {
      accent: 'blue-500',
      accentDark: 'blue-600',
      accentLight: 'blue-400',
      bg: 'blue-950',
      bgLight: 'blue-900'
    }
  },
  forest: {
    id: 'forest',
    name: 'Forest Green',
    description: 'Natural and growth-oriented',
    colors: {
      primary: 'emerald',
      secondary: 'teal',
      accent: '#10b981',
      accentHover: '#059669',
      background: '#ffffff',
      backgroundDark: '#0a1f1a',
      backgroundMuted: '#ecfdf5',
      text: '#0a1f1a',
      textLight: '#ffffff',
      textMuted: '#64748b',
      border: '#a7f3d0'
    },
    gradients: {
      hero: 'linear-gradient(135deg, #0a1f1a 0%, #134e4a 50%, #115e59 100%)',
      heroClass: 'from-emerald-950 via-teal-900 to-emerald-900',
      card: 'from-white to-emerald-50',
      button: 'from-emerald-500 to-teal-500'
    },
    tailwind: {
      accent: 'emerald-500',
      accentDark: 'emerald-600',
      accentLight: 'emerald-400',
      bg: 'emerald-950',
      bgLight: 'teal-900'
    }
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Warmth',
    description: 'Warm and inviting orange tones',
    colors: {
      primary: 'orange',
      secondary: 'rose',
      accent: '#f97316',
      accentHover: '#ea580c',
      background: '#ffffff',
      backgroundDark: '#1c1210',
      backgroundMuted: '#fff7ed',
      text: '#1c1210',
      textLight: '#ffffff',
      textMuted: '#64748b',
      border: '#fed7aa'
    },
    gradients: {
      hero: 'linear-gradient(135deg, #1c1210 0%, #7c2d12 50%, #9f1239 100%)',
      heroClass: 'from-orange-950 via-orange-900 to-rose-900',
      card: 'from-white to-orange-50',
      button: 'from-orange-500 to-rose-500'
    },
    tailwind: {
      accent: 'orange-500',
      accentDark: 'orange-600',
      accentLight: 'orange-400',
      bg: 'orange-950',
      bgLight: 'orange-900'
    }
  },
  midnight: {
    id: 'midnight',
    name: 'Midnight Purple',
    description: 'Elegant and modern purple',
    colors: {
      primary: 'violet',
      secondary: 'purple',
      accent: '#8b5cf6',
      accentHover: '#7c3aed',
      background: '#ffffff',
      backgroundDark: '#13111c',
      backgroundMuted: '#f5f3ff',
      text: '#13111c',
      textLight: '#ffffff',
      textMuted: '#64748b',
      border: '#ddd6fe'
    },
    gradients: {
      hero: 'linear-gradient(135deg, #13111c 0%, #4c1d95 50%, #581c87 100%)',
      heroClass: 'from-violet-950 via-purple-900 to-indigo-900',
      card: 'from-white to-violet-50',
      button: 'from-violet-500 to-purple-500'
    },
    tailwind: {
      accent: 'violet-500',
      accentDark: 'violet-600',
      accentLight: 'violet-400',
      bg: 'violet-950',
      bgLight: 'purple-900'
    }
  }
};

const themeOrder = ['classic', 'ocean', 'forest', 'sunset', 'midnight'];

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('classic');
  const [autoRotate, setAutoRotate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load theme settings from localStorage and API
  useEffect(() => {
    const loadThemeSettings = async () => {
      try {
        // First check localStorage for quick load
        const savedTheme = localStorage.getItem('portfolioTheme');
        const savedAutoRotate = localStorage.getItem('portfolioAutoRotate');
        
        if (savedAutoRotate === 'true') {
          setAutoRotate(true);
          // Auto-rotate: use day of year to select theme
          const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
          const themeIndex = dayOfYear % themeOrder.length;
          setCurrentTheme(themeOrder[themeIndex]);
        } else if (savedTheme && themes[savedTheme]) {
          setCurrentTheme(savedTheme);
        }
      } catch (error) {
        console.error('Error loading theme settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadThemeSettings();
  }, []);

  // Apply theme CSS variables
  useEffect(() => {
    const theme = themes[currentTheme];
    if (!theme) return;

    document.documentElement.setAttribute('data-theme', currentTheme);
    
    // Apply CSS custom properties for the theme
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-secondary', theme.colors.secondary);
  }, [currentTheme]);

  const changeTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
      setAutoRotate(false);
      localStorage.setItem('portfolioTheme', themeId);
      localStorage.setItem('portfolioAutoRotate', 'false');
    }
  };

  const toggleAutoRotate = (enabled) => {
    setAutoRotate(enabled);
    localStorage.setItem('portfolioAutoRotate', enabled ? 'true' : 'false');
    
    if (enabled) {
      // Set theme based on day
      const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
      const themeIndex = dayOfYear % themeOrder.length;
      const newTheme = themeOrder[themeIndex];
      setCurrentTheme(newTheme);
      localStorage.setItem('portfolioTheme', newTheme);
    }
  };

  const getNextTheme = () => {
    const currentIndex = themeOrder.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    return themeOrder[nextIndex];
  };

  const theme = themes[currentTheme] || themes.classic;

  return (
    <ThemeContext.Provider value={{
      currentTheme,
      theme,
      themes,
      themeOrder,
      autoRotate,
      isLoading,
      changeTheme,
      toggleAutoRotate,
      getNextTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
